import React, { useState } from "react";
import GeoAreaPicker from "../geo/GeoAreaPicker";
import { CoverageArea, coverageKey, areaLabel } from "../../utils/coverage";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
    coverage: CoverageArea[];
    /** Persists the full coverage list (replaces it). */
    onSave: (coverage: CoverageArea[]) => Promise<void>;
    disabled?: boolean;
}

/** Lets a provider declare where they operate: add areas at any level, remove
 *  them. Each change replaces the whole coverage list on the listing. */
const ServiceAreaEditor: React.FC<Props> = ({ token, coverage, onSave, disabled }) => {
    const [pending, setPending] = useState<CoverageArea | null>(null); // deepest picked node
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const busyOrDisabled = busy || !!disabled;

    const save = async (next: CoverageArea[]) => {
        setBusy(true);
        setError(null);
        try {
            await onSave(next);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const addPending = () => {
        if (!pending) return;
        const key = coverageKey(pending.level, pending.id);
        if (coverage.some((c) => coverageKey(c.level, c.id) === key)) return; // dedupe
        save([...coverage, pending]);
    };

    const remove = (c: CoverageArea) => {
        const key = coverageKey(c.level, c.id);
        save(coverage.filter((x) => coverageKey(x.level, x.id) !== key));
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {coverage.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                    {coverage.map((c) => (
                        <span key={coverageKey(c.level, c.id)} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", background: "var(--surface-2, #f3f4f6)", borderRadius: "999px", padding: "0.15rem 0.6rem", fontSize: "0.85rem" }}>
                            {areaLabel(c)}
                            <button type="button" className="btn-sm" onClick={() => remove(c)} disabled={busyOrDisabled} aria-label={`Remove ${c.name}`}>✕</button>
                        </span>
                    ))}
                </div>
            ) : (
                <small style={{ color: "var(--text-secondary)" }}>No service area yet — add the places you cover.</small>
            )}

            <div style={{ border: "1px solid var(--border, #ddd)", borderRadius: "6px", padding: "0.6rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <strong style={{ fontSize: "0.85rem" }}>Add an area you cover</strong>
                <GeoAreaPicker token={token} context="provider" onChange={(chain) => setPending(chain.length ? chain[chain.length - 1] : null)} />
                <button type="button" onClick={addPending} disabled={busyOrDisabled || !pending} style={{ alignSelf: "flex-start" }}>
                    {busy ? "Saving…" : pending ? `Add ${areaLabel(pending)}` : "Pick an area to add"}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}
        </div>
    );
};

export default ServiceAreaEditor;
